/* eslint-disable id-length */
import type { Color, Hsv, Rgb } from 'culori/fn';
import { wcagContrast, differenceEuclidean, formatHex } from 'culori/fn';
import {
  DEFAULT_COLOR_FOR_COMPARISON,
  DEFAULT_MIN_CONTRAST,
  DEFAULT_OPACITY_FOR_CONTRAST_CALCULATION,
} from './consts';
import { convertToHsv, convertToRgb } from './color-conversion-utils';

// Once we reach a contrast ratio of 4.504, (assuming the default min of 4.50)
// which will round down to 4.50 with toFixed(2), bail on trying to get a
// brighter color.
const CONTRAST_EPSILON = 0.004;

// Threshold for snapping value channel to 0 or 1
const VALUE_CHANNEL_SNAP_EPSILON = 0.0005;

type PropsForGetAccessibleColor = {
  originalColor: Color | string;
  colorForComparison: string;
  minContrast: number;
  maxIterations?: number;
  opacityForContrastCalculation: number;
  /**
   * Converting to hex reduces precision, but we do it because the ColorPicker's
   * API deals in hex. If we didn't do this, we'd be able to achieve the minimum
   * contrast ratio more precisely (like instead of overshooting it a little bit
   * sometimes), but the color we return would get a slightly different contrast
   * ratio when converted to hex.
   */
  shouldConvertToHex?: boolean;
};

type Direction = 'brighter' | 'darker';

export const compositeOverWhite = (color: Color | string, alpha = 1): Rgb => {
  const compositeChannel = (channel: number) => alpha * channel + (1 - alpha);

  const colorAsRgb = convertToRgb(color);

  return {
    mode: 'rgb' as const,
    r: compositeChannel(colorAsRgb.r),
    g: compositeChannel(colorAsRgb.g),
    b: compositeChannel(colorAsRgb.b),
  };
};

export const calculateContrast = ({
  backgroundColor,
  foregroundColor = DEFAULT_COLOR_FOR_COMPARISON,
  backgroundOpacity = DEFAULT_OPACITY_FOR_CONTRAST_CALCULATION,
}: {
  backgroundColor: Color | string;
  foregroundColor?: Color | string;
  backgroundOpacity?: number;
}): number => {
  const backgroundColorAsRgb = convertToRgb(backgroundColor);
  const foregroundColorAsRgb = convertToRgb(foregroundColor);

  const compositeBackground = compositeOverWhite(backgroundColorAsRgb, backgroundOpacity);
  return wcagContrast(compositeBackground, foregroundColorAsRgb);
};

const snapValueToEdges = (value: number): number => {
  if (value > 1 - VALUE_CHANNEL_SNAP_EPSILON) {
    return 1;
  }
  if (value < VALUE_CHANNEL_SNAP_EPSILON) {
    return 0;
  }
  return value;
};

const updateSearchBounds = (
  searchingBrighter: boolean,
  contrast: number,
  minContrast: number,
  midValue: number,
  searchLow: number,
  searchHigh: number,
): { newSearchLow: number; newSearchHigh: number } => {
  if (contrast >= minContrast) {
    if (searchingBrighter) {
      return { newSearchLow: searchLow, newSearchHigh: midValue };
    }
    return { newSearchLow: midValue, newSearchHigh: searchHigh };
  }

  if (searchingBrighter) {
    return { newSearchLow: midValue, newSearchHigh: searchHigh };
  }
  return { newSearchLow: searchLow, newSearchHigh: midValue };
};

export const getAccessibleColorByValueChannelChange = ({
  originalColor,
  colorForComparison,
  minContrast,
  maxIterations = 50,
  direction,
  opacityForContrastCalculation,
  shouldConvertToHex = true,
}: PropsForGetAccessibleColor & { direction: Direction }): Hsv | null => {
  const originalHsv = convertToHsv(originalColor);

  const searchingBrighter = direction === 'brighter';

  // Set up search bounds and extreme variant (only changing value, keeping saturation)
  const lowValue = searchingBrighter ? originalHsv.v : 0;
  const highValue = searchingBrighter ? 1 : originalHsv.v;
  const extremeValue = searchingBrighter ? 1 : 0;
  const extremeVariant: Hsv = {
    h: originalHsv.h ?? 0,
    s: originalHsv.s,
    v: extremeValue,
    mode: 'hsv' as const,
  };

  // Check if extreme variant meets contrast requirement

  const extremeContrast = calculateContrast({
    backgroundColor: shouldConvertToHex ? formatHex(extremeVariant) : extremeVariant,
    foregroundColor: colorForComparison,
    backgroundOpacity: opacityForContrastCalculation,
  });

  // If extreme doesn't work, there's no accessible color in this direction via value change
  if (extremeContrast < minContrast) {
    return null;
  }

  let searchLow = lowValue;
  let searchHigh = highValue;
  let bestSoFar: Hsv = extremeVariant;

  for (let i = 0; i < maxIterations; i++) {
    const rawMidValue = (searchLow + searchHigh) / 2;
    const midValue = snapValueToEdges(rawMidValue);

    const candidateHsv: Hsv = {
      h: originalHsv.h ?? 0,
      s: originalHsv.s,
      v: midValue,
      mode: 'hsv' as const,
    };

    const contrast = calculateContrast({
      backgroundColor: shouldConvertToHex ? formatHex(candidateHsv) : candidateHsv,
      foregroundColor: colorForComparison,
      backgroundOpacity: opacityForContrastCalculation,
    });

    if (contrast >= minContrast) {
      bestSoFar = candidateHsv;
    }

    const { newSearchLow, newSearchHigh } = updateSearchBounds(
      searchingBrighter,
      contrast,
      minContrast,
      midValue,
      searchLow,
      searchHigh,
    );

    searchLow = newSearchLow;
    searchHigh = newSearchHigh;

    if (contrast >= minContrast && contrast - minContrast < CONTRAST_EPSILON) {
      break;
    }
  }

  return bestSoFar;
};

const chooseClosestDerivativeToOriginal = (
  brighterOption: Hsv | null,
  darkerOption: Hsv | null,
  originalColor: Color | string,
): Hsv | null => {
  const hsvDifference = differenceEuclidean('hsv');

  if (!brighterOption && !darkerOption) {
    return null;
  }

  // If only one option works, use it
  if (!brighterOption && darkerOption != null) {
    return darkerOption;
  }

  if (!darkerOption && brighterOption != null) {
    return brighterOption;
  }

  const brighterDifference =
    brighterOption === null ? Infinity : hsvDifference(originalColor, brighterOption);

  const darkerDifference =
    darkerOption === null ? Infinity : hsvDifference(originalColor, darkerOption);

  if (brighterDifference <= darkerDifference && brighterOption != null) {
    return brighterOption;
  }

  if (darkerDifference < brighterDifference && darkerOption != null) {
    return darkerOption;
  }

  throw new Error('Unable to determine the best accessible color variant');
};

const getAccessibleColorBySaturationAndValueChange = ({
  originalColor,
  colorForComparison,
  minContrast,
  direction,
  opacityForContrastCalculation,
}: Omit<PropsForGetAccessibleColor, 'maxIterations'> & { direction: Direction }): Hsv | null => {
  const originalHsv = convertToHsv(originalColor);

  const hsvDifference = differenceEuclidean('hsv');
  const maxIterations = 50;

  // Define search bounds based on direction
  let lowStep = 0;
  let highStep = 1;

  // Check if extreme position meets contrast requirement
  const getHsvForStep = (step: number): Hsv => {
    if (direction === 'brighter') {
      const saturationChannel = Math.max(0, originalHsv.s - step);
      const valueChannel = Math.min(1, originalHsv.v + step);
      return {
        h: originalHsv.h ?? 0,
        s: saturationChannel,
        v: valueChannel,
        mode: 'hsv' as const,
      };
    }

    const saturationChannel = Math.min(1, originalHsv.s + step);
    const valueChannel = Math.max(0, originalHsv.v - step);
    return {
      h: originalHsv.h ?? 0,
      s: saturationChannel,
      v: valueChannel,
      mode: 'hsv' as const,
    };
  };

  const checkContrast = (hsv: Hsv): number => {
    return calculateContrast({
      backgroundColor: formatHex(hsv),
      foregroundColor: colorForComparison,
      backgroundOpacity: opacityForContrastCalculation,
    });
  };

  // Check if extreme case meets contrast requirement
  const extremeHsv = getHsvForStep(highStep);
  if (checkContrast(extremeHsv) < minContrast) {
    return null; // No solution exists
  }

  let bestCandidate: Hsv | null = null;
  let smallestDifference = Infinity;

  // Binary search for the closest accessible color
  for (let i = 0; i < maxIterations; i++) {
    const midStep = (lowStep + highStep) / 2;
    const candidateHsv = getHsvForStep(midStep);
    const contrast = checkContrast(candidateHsv);

    if (contrast >= minContrast) {
      const difference = hsvDifference(originalHsv, candidateHsv);

      if (difference < smallestDifference) {
        bestCandidate = candidateHsv;
        smallestDifference = difference;
      }

      // Try to get closer to original (smaller step)
      highStep = midStep;

      // If we're very close to minimum contrast, we can stop
      if (contrast - minContrast < CONTRAST_EPSILON) {
        break;
      }
    } else {
      // Need more adjustment (larger step)
      lowStep = midStep;
    }
  }

  return bestCandidate;
};

const getAccessibleColorInDirection = ({
  originalColor,
  colorForComparison,
  minContrast,
  maxIterations = 50,
  direction,
  opacityForContrastCalculation,
}: PropsForGetAccessibleColor & { direction: 'brighter' | 'darker' }): Hsv | null => {
  // First try changing only the value channel
  const valueResult = getAccessibleColorByValueChannelChange({
    originalColor,
    colorForComparison,
    minContrast,
    maxIterations,
    direction,
    opacityForContrastCalculation,
  });

  if (valueResult !== null) {
    return valueResult;
  }

  return getAccessibleColorBySaturationAndValueChange({
    originalColor,
    colorForComparison,
    minContrast,
    direction,
    opacityForContrastCalculation,
  });
};

/**
 * Finds the accessible color that is closest to the original color by trying both
 * brighter and darker variants and choosing the one with the smallest change in value.
 */
export const getAccessibleColor = ({
  originalColor,
  colorForComparison,
  minContrast = DEFAULT_MIN_CONTRAST,
  maxIterations = 50,
  opacityForContrastCalculation,
}: PropsForGetAccessibleColor): Hsv => {
  const originalColorAsHsv = convertToHsv(originalColor);

  const originalContrast = calculateContrast({
    backgroundColor: formatHex(originalColorAsHsv),
    foregroundColor: colorForComparison,
    backgroundOpacity: opacityForContrastCalculation,
  });

  if (originalContrast >= minContrast) {
    return convertToHsv(originalColor);
  }

  const brighterOption = getAccessibleColorInDirection({
    originalColor,
    colorForComparison,
    minContrast,
    maxIterations,
    direction: 'brighter',
    opacityForContrastCalculation,
  });

  const darkerOption = getAccessibleColorInDirection({
    originalColor,
    colorForComparison,
    minContrast,
    maxIterations,
    direction: 'darker',
    opacityForContrastCalculation,
  });

  const result = chooseClosestDerivativeToOriginal(brighterOption, darkerOption, originalColor);

  if (result == null) {
    throw new Error(
      `Unable to get accessible derivative for ${formatHex(originalColor)} at minimum contrast ratio ${minContrast}`,
    );
  }

  return result;
};

/**
 * Checks if any accessible color derivatives exist for the given parameters.
 * Returns true if at least one accessible color can be found by going brighter or darker.
 */
export const hasAccessibleDerivatives = ({
  originalColor,
  colorForComparison,
  minContrast = DEFAULT_MIN_CONTRAST,
  opacityForContrastCalculation,
}: PropsForGetAccessibleColor): boolean => {
  const originalColorAsHsv = convertToHsv(originalColor);
  // Check if original color already meets min contrast
  const originalContrast = calculateContrast({
    backgroundColor: formatHex(originalColorAsHsv),
    foregroundColor: colorForComparison,
    backgroundOpacity: opacityForContrastCalculation,
  });

  if (originalContrast >= minContrast) {
    return true;
  }

  // Try getting a brighter version (higher value)
  const brighterOption = getAccessibleColorInDirection({
    originalColor,
    colorForComparison,
    minContrast,
    direction: 'brighter',
    opacityForContrastCalculation,
  });

  // Try getting a darker version (lower value)
  const darkerOption = getAccessibleColorInDirection({
    originalColor,
    colorForComparison,
    minContrast,
    direction: 'darker',
    opacityForContrastCalculation,
  });

  return brighterOption !== null || darkerOption !== null;
};
